<script setup lang="ts">
import ShimmerLoader from '@/components/common/animations/ShimmerLoader.vue'
import GlobeIcon from '@/components/common/icons/GlobeIcon.vue'
import { truncateString } from '@/components/helper'
import { GroupState } from '@/composition/groups'
import { DEFAULT_GROUP_LOGO } from '@/helper/const'
import { replaceBucketUrlWithCdnUrl } from '@/helper/ImageUpload.helper'
import { isGokollab } from '@ghl/clientportal-core/init'
import {
  Compass03Icon,
  Lock03Icon,
  Pin02Icon,
  PlusCircleIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIAvatar,
  UITextMdMedium,
  UITextXsMedium,
  UITextXsRegular,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import { computed, h, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

enum GroupType {
  PUBLIC = 'Public',
  PRIVATE = 'Private',
}

const router = useRouter()
const { t } = useI18n()
const pagesize = 30
const isGroupListLoading = ref(false)

let groupList = computed<
  {
    id: string
    key: string
    label: string
    icon: any
    imageUrl: string
    type: GroupType
    totalMembers: number
    position: number
  }[]
>(() => {
  return GroupState.state.groupList.map((group: any) => {
    let position = 0
    const groupOption = {
      id: group._id.toString(),
      key: group.slug,
      label: truncateString(group.name, 30),
      imageUrl:
        replaceBucketUrlWithCdnUrl(group?.branding?.logo) || DEFAULT_GROUP_LOGO,
      icon: () => {
        const groupAvatar = h(UIAvatar, {
          style: {
            margin: '8px',
          },
          size: 24,
          class: 'font-medium text-communities-background  text-md',
          src:
            replaceBucketUrlWithCdnUrl(group?.branding?.logo) ||
            DEFAULT_GROUP_LOGO,
        })
        return groupAvatar
      },
      type:
        group.settings?.groupType?.toLowerCase() === 'private'
          ? GroupType.PRIVATE
          : GroupType.PUBLIC,
      totalMembers: group.membersCount,
      position: position++,
    }
    return groupOption
  })
})

watch(groupList, async () => {
  await GroupState.computeUnpinnedGroups()
})

onMounted(async () => {
  await registerObserver2()
  await GroupState.fetchPinnedGroups()
})

function handleCreateCommunity() {
  window.location.href = '/create-community'
}
function handleDiscoverCommunity() {
  window.location.href = '/discovery'
}
function onSelect(groupSlug: string) {
  if (GroupState.state.groupData?.slug === groupSlug) {
    return
  }
  router.push({ name: 'home', params: { groupSlug: groupSlug } })
  // redirectToGroup(groupSlug)
}
async function registerObserver2() {
  nextTick(async () => {
    const observer = new IntersectionObserver(
      async entries => {
        if (entries[0].isIntersecting && !isGroupListLoading.value) {
          isGroupListLoading.value = true
          await GroupState.getGroupByLocation({ limit: pagesize })
          await GroupState.computeUnpinnedGroups()
          isGroupListLoading.value = false
        }
      },
      {
        rootMargin: '80px', // Increased margin to trigger earlier
        threshold: 0.5,
      }
    )
    const target = document.getElementById('observe-list-end')
    if (target) {
      observer.observe(target)
    }
  })
}
</script>

<template>
  <div
    class="h-full border-b-0 border-l-0 border-t-0 border-solid border-communities-sidebar-border"
  >
    <div class="sticky z-20 h-[calc(100vh-58px)] max-w-[58px]">
      <div
        class="mt-[58px] flex h-full w-[58px] flex-col items-center justify-start overflow-y-auto"
      >
        <!-- CREATE COMMUNITY ICON -->
        <UITooltip
          v-if="isGokollab()"
          :placement="'right-start'"
          trigger="hover"
        >
          <template #trigger>
            <button
              :id="'create-community'"
              class="mt-[12px] flex h-9 w-9 items-center justify-center rounded-lg border border-solid border-communities-border bg-communities-primary p-1 text-communities-sidebar-selected-item-font"
              @click="handleCreateCommunity"
            >
              <PlusCircleIcon class="h-6 w-6" />
            </button>
          </template>
          <div
            class="flex h-[33px] max-w-[240px] flex-col items-center justify-center rounded-lg border border-solid border-communities-sidebar-border pl-2 pr-2 text-center"
          >
            <UITextMdMedium
              class="line-clamp-1 text-[12px] font-semibold leading-[20px]"
              >Create Group</UITextMdMedium
            >
          </div>
        </UITooltip>

        <!-- DISCOVER COMMUNITY ICON -->
        <UITooltip
          v-if="isGokollab()"
          :placement="'right-start'"
          trigger="hover"
        >
          <template #trigger>
            <button
              :id="'discover'"
              class="mt-[12px] flex h-9 w-9 items-center justify-center rounded-lg border border-solid border-communities-border bg-communities-primary p-1 text-communities-sidebar-selected-item-font"
              @click="handleDiscoverCommunity"
            >
              <Compass03Icon class="h-6 w-6" />
            </button>
          </template>
          <div
            class="flex h-[33px] max-w-[240px] flex-col items-center justify-center rounded-lg border border-solid border-communities-sidebar-border pl-2 pr-2 text-center"
          >
            <UITextMdMedium
              class="line-clamp-1 text-[12px] font-semibold leading-[20px]"
              >Discover</UITextMdMedium
            >
          </div>
        </UITooltip>

        <!-- PINNED GROUP LIST -->
        <div
          v-for="(group, index) in GroupState.state.pinnedGroupList"
          :key="group?.key"
          class="mt-[12px]"
        >
          <UITooltip :placement="'right-start'" trigger="hover">
            <template #trigger>
              <div
                class="flex h-[36px] w-[36px] cursor-pointer items-center justify-center"
                role="button"
                tabindex="0"
                @click="onSelect(group?.slug)"
                @keydown.space="onSelect(group?.slug)"
              >
                <div class="relative h-[36px] w-[36px]">
                  <img
                    :alt="group?.name"
                    :src="group?.logo || DEFAULT_GROUP_LOGO"
                    class="h-[36px] w-[36px] rounded-lg"
                  />
                  <div
                    class="absolute -right-[4px] -top-[4px] flex h-[16px] w-[16px] items-center justify-center rounded-full bg-communities-primary text-communities-sidebar-selected-item-font"
                  >
                    <Pin02Icon
                      class="communities-sidebar-font-primary h-[8px] w-[8px]"
                    />
                  </div>
                </div>
              </div>
            </template>
            <div
              class="flex h-[45px] max-w-[240px] flex-col items-center justify-center rounded-lg border border-solid border-communities-sidebar-border pl-2 pr-2 text-center"
            >
              <UITextXsMedium
                class="line-clamp-1 font-semibold leading-[20px]"
                >{{ group?.name }}</UITextXsMedium
              >
              <UITextXsRegular
                class="line-clamp-1 text-[10px] font-normal leading-[14px]"
              >
                <GlobeIcon
                  v-if="group?.type === GroupType.PUBLIC"
                  class="h-[9px] w-[9px]"
                />
                <Lock03Icon
                  v-if="group?.type === GroupType.PRIVATE"
                  class="h-[9px] w-[9px]"
                />
                {{ t(`common.${group?.type.toLocaleLowerCase()}`) }} •
                {{ group?.membersCount || 0 }}
                {{ t('common.members') }}
              </UITextXsRegular>
            </div>
          </UITooltip>
          <ShimmerLoader
            v-if="
              GroupState.state.unpinnedGroupList.length === 0 &&
              index === GroupState.state.pinnedGroupList.length - 1 &&
              isGroupListLoading
            "
            :repeat="2"
            :is-loading="true"
            class-name="h-[36px] w-[36px] mt-[12px] rounded-lg"
          />
        </div>

        <!-- UNPINNED GROUP LIST -->
        <div
          v-for="(group, index) in GroupState.state.unpinnedGroupList"
          v-if="!isGokollab()"
          :key="group?.key"
          class="mt-[12px]"
        >
          <UITooltip :placement="'right-start'" trigger="hover">
            <template #trigger>
              <div
                class="flex h-[36px] w-[36px] cursor-pointer items-center justify-center"
                role="button"
                tabindex="0"
                @click="onSelect(group?.slug)"
                @keydown.space="onSelect(group?.slug)"
              >
                <img
                  :alt="group?.name"
                  :src="group?.logo || DEFAULT_GROUP_LOGO"
                  class="h-[36px] w-[36px] rounded-lg"
                />
              </div>
            </template>
            <div
              class="flex h-[45px] max-w-[240px] flex-col items-center justify-center rounded-lg border border-solid border-communities-sidebar-border pl-2 pr-2 text-center"
            >
              <UITextXsMedium
                class="line-clamp-1 font-semibold leading-[20px]"
                >{{ group?.name }}</UITextXsMedium
              >
              <UITextXsRegular
                class="line-clamp-1 text-[10px] font-normal leading-[14px]"
              >
                <GlobeIcon
                  v-if="group?.type === GroupType.PUBLIC"
                  class="h-[9px] w-[9px]"
                />
                <Lock03Icon
                  v-if="group?.type === GroupType.PRIVATE"
                  class="h-[9px] w-[9px]"
                />
                {{ t(`common.${group?.type.toLocaleLowerCase()}`) }} •
                {{ group?.membersCount || 0 }}
                {{ t('common.members') }}
              </UITextXsRegular>
            </div>
          </UITooltip>
          <ShimmerLoader
            v-if="
              index === GroupState.state.unpinnedGroupList.length - 1 &&
              isGroupListLoading
            "
            :repeat="2"
            :is-loading="true"
            class-name="h-[36px] w-[36px] mt-[12px] rounded-lg shimmer-group-list"
          />
        </div>

        <!-- SCROLLABLE LIST ENDS HERE -->

        <div id="observe-list-end" class="h-[58px] w-full" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 0px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}
</style>

<style>
.shimmer-group-list.shimmer {
  background: var(--sidebar-background-gradient);
  background-image: linear-gradient(
    to right,
    var(--sidebar-background-gradient) 0%,
    var(--sidebar-background) 20%,
    var(--sidebar-background-gradient) 40%,
    var(--sidebar-background-gradient) 100%
  );
}
</style>
